import {MoleculeSchemaInterfaceOption} from 'chemistry/schema-configuration/MoleculeSchemaInterfaceConfiguration';
import {MoleculeSchemaInterfaceApiId} from 'chemistry/schema-configuration/MoleculeSchemaInterfaceConstants';
import {ClientTagSchemaConstraint} from 'constants/RegistryConstants';
import {TypeSpecificConstraintType} from 'graphql-client/__generated/graphqlGlobalTypes';

const EMPTY_MOLECULE_CONSTRAINT_BASE: Pick<
  ClientTagSchemaConstraint,
  'uniqueResidues' | 'areUniqueResiduesCaseSensitive' | 'fields'
> = {
  uniqueResidues: false,
  areUniqueResiduesCaseSensitive: false,
  fields: [],
};

export function createDefaultCompoundMoleculeSchemaConstraint(): ClientTagSchemaConstraint {
  return {
    ...EMPTY_MOLECULE_CONSTRAINT_BASE,
    uniqueCanonicalSmiles: true,
  };
}

export function createDefaultFormMoleculeSchemaConstraint(): ClientTagSchemaConstraint {
  return {
    ...EMPTY_MOLECULE_CONSTRAINT_BASE,
    uniqueCanonicalSmiles: true,
    typeSpecificConstraintConfig: {
      constraintType: TypeSpecificConstraintType.UNIQUE_SALT_SOLVATE,
    },
  };
}

export function hasSaltSolventConstraint(constraint: ClientTagSchemaConstraint): boolean {
  return (
    constraint.typeSpecificConstraintConfig?.constraintType === TypeSpecificConstraintType.UNIQUE_SALT_SOLVATE
  );
}

function isConstraintEmpty(constraint: ClientTagSchemaConstraint): boolean {
  return (
    !constraint.uniqueCanonicalSmiles &&
    !constraint.uniqueResidues &&
    constraint.fields.length === 0 &&
    constraint.typeSpecificConstraintConfig == null
  );
}

export function removeSaltSolventFromConstraint(
  constraint: ClientTagSchemaConstraint | null
): ClientTagSchemaConstraint | null {
  if (constraint == null) {
    return null;
  }
  if (!hasSaltSolventConstraint(constraint)) {
    return constraint;
  }
  const {typeSpecificConstraintConfig: _removed, ...rest} = constraint;
  const updated: ClientTagSchemaConstraint = rest;
  return isConstraintEmpty(updated) ? null : updated;
}

/**
 * Updates the schema unique constraint when the user selects a molecule SSI.
 * Molecule Compound requires canonical SMILES uniqueness; Molecule Form additionally
 * requires salt/solvent composition uniqueness.
 */
export function computeUpdatedConstraintForMoleculeSchemaInterfaceChange(
  moleculeSchemaInterface: MoleculeSchemaInterfaceOption,
  existingConstraint: ClientTagSchemaConstraint | null
): ClientTagSchemaConstraint | null {
  if (moleculeSchemaInterface === MoleculeSchemaInterfaceApiId.MOLECULE_COMPOUND_ID) {
    const withoutSaltSolvent = removeSaltSolventFromConstraint(existingConstraint);
    if (withoutSaltSolvent == null) {
      return createDefaultCompoundMoleculeSchemaConstraint();
    }
    return {
      ...withoutSaltSolvent,
      uniqueCanonicalSmiles: true,
    };
  } else if (moleculeSchemaInterface === MoleculeSchemaInterfaceApiId.MOLECULE_FORM_ID) {
    if (existingConstraint == null) {
      return createDefaultFormMoleculeSchemaConstraint();
    }
    return {
      ...existingConstraint,
      uniqueCanonicalSmiles: true,
      typeSpecificConstraintConfig: hasSaltSolventConstraint(existingConstraint)
        ? existingConstraint.typeSpecificConstraintConfig
        : {constraintType: TypeSpecificConstraintType.UNIQUE_SALT_SOLVATE},
    };
  } else {
    return removeSaltSolventFromConstraint(existingConstraint);
  }
}

export function shouldNotifySaltSolventConstraintRemoved(
  previousConstraint: ClientTagSchemaConstraint | null,
  nextConstraint: ClientTagSchemaConstraint | null,
  moleculeSchemaInterface: MoleculeSchemaInterfaceOption
): boolean {
  return (
    moleculeSchemaInterface !== MoleculeSchemaInterfaceApiId.MOLECULE_FORM_ID &&
    previousConstraint != null &&
    hasSaltSolventConstraint(previousConstraint) &&
    (nextConstraint == null || !hasSaltSolventConstraint(nextConstraint))
  );
}
